/**
 * ProductCard Component
 * Displays a product card with image, name, price, and commission info
 * Used in Affiliate Marketplace
 */

import React from 'react';
import {
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Typography,
  Button,
  Box,
  Chip,
} from '@mui/material';
import {
  Favorite as FavoriteIcon,
  FavoriteBorder as FavoriteBorderIcon,
  ShoppingCart as ShoppingCartIcon,
} from '@mui/icons-material';
import { useDarkMode } from '../../hooks/useDarkMode';
import CommissionBadge from './CommissionBadge';

export interface ProductCardProps {
  productId: number;
  productName: string;
  price: number;
  mainImage: string;
  commissionRate: number;
  commissionType: 'percentage' | 'fixed';
  brandName: string;
  brandImage?: string;
  keywords?: string[];
  isInWishlist?: boolean;
  hasRequested?: boolean;
  isOwnProduct?: boolean; // 본인 상품 여부
  // Affiliate settings
  affiliateStartDate?: string;
  affiliateEndDate?: string;
  affiliateMaxUses?: number;
  affiliateCurrentUses?: number;
  onAddToWishlist?: (productId: number) => void;
  onRemoveFromWishlist?: (productId: number) => void;
  onRequestAccess?: (productId: number) => void;
  onClick?: (productId: number) => void;
}

export default function ProductCard({
  productId,
  productName,
  price,
  mainImage,
  commissionRate,
  commissionType,
  brandName,
  brandImage,
  keywords = [],
  isInWishlist = false,
  hasRequested = false,
  isOwnProduct = false,
  affiliateStartDate,
  affiliateEndDate,
  affiliateMaxUses,
  affiliateCurrentUses,
  onAddToWishlist,
  onRemoveFromWishlist,
  onRequestAccess,
  onClick,
}: ProductCardProps) {
  const isDarkMode = useDarkMode();

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const handleWishlistClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isInWishlist && onRemoveFromWishlist) {
      onRemoveFromWishlist(productId);
    } else if (!isInWishlist && onAddToWishlist) {
      onAddToWishlist(productId);
    }
  };

  const handleRequestClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onRequestAccess) {
      onRequestAccess(productId);
    }
  };

  const handleCardClick = () => {
    if (onClick) {
      onClick(productId);
    }
  };

  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'transform 0.2s, box-shadow 0.2s',
        bgcolor: isDarkMode ? '#1e1e1e' : '#fff',
        '&:hover': onClick ? {
          transform: 'translateY(-4px)',
          boxShadow: 4,
        } : {},
      }}
      onClick={handleCardClick}
    >
      {/* Product Image */}
      <CardMedia
        component="img"
        height="200"
        image={mainImage || '/placeholder-product.png'}
        alt={productName}
        sx={{ objectFit: 'cover' }}
      />

      {/* Card Content */}
      <CardContent sx={{ flexGrow: 1, pb: 1 }}>
        {/* Product Name */}
        <Typography
          variant="h6"
          component="div"
          sx={{
            color: isDarkMode ? '#fff' : '#000',
            fontWeight: 600,
            mb: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
          }}
        >
          {productName}
        </Typography>

        {/* Price and Commission */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="h6" sx={{ color: isDarkMode ? '#fff' : '#000', fontWeight: 'bold' }}>
            ₩{price.toLocaleString()}
          </Typography>
          <CommissionBadge rate={commissionRate} type={commissionType} />
        </Box>

        {/* Brand Info */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          {brandImage && (
            <Box
              component="img"
              src={brandImage}
              alt={brandName}
              sx={{
                width: 24,
                height: 24,
                borderRadius: '50%',
                objectFit: 'cover',
              }}
            />
          )}
          <Typography variant="body2" sx={{ color: isDarkMode ? '#aaa' : '#666' }}>
            {brandName}
          </Typography>
        </Box>

        {/* Keywords */}
        {keywords && keywords.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
            {keywords.map((keyword, index) => (
              <Chip
                key={`keyword-${index}`}
                label={keyword}
                size="small"
                sx={{
                  bgcolor: '#000',
                  color: '#fff',
                  height: 20,
                  fontSize: '0.7rem',
                }}
              />
            ))}
          </Box>
        )}

        {/* Affiliate Settings Info */}
        <Box sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1.5,
          p: 1,
          borderRadius: 1,
          bgcolor: isDarkMode ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
        }}>
          <Box sx={{ flex: 1, minWidth: 80 }}>
            <Typography variant="caption" sx={{ color: isDarkMode ? '#aaa' : '#666', display: 'block', fontSize: '0.65rem' }}>
              게시 기간
            </Typography>
            <Typography variant="body2" sx={{ color: isDarkMode ? '#fff' : '#000', fontWeight: 500, fontSize: '0.75rem' }}>
              {affiliateStartDate || affiliateEndDate
                ? `${affiliateStartDate ? formatDate(affiliateStartDate) : '-'} ~ ${affiliateEndDate ? formatDate(affiliateEndDate) : '-'}`
                : '-'
              }
            </Typography>
          </Box>
          <Box sx={{ minWidth: 60 }}>
            <Typography variant="caption" sx={{ color: isDarkMode ? '#aaa' : '#666', display: 'block', fontSize: '0.65rem' }}>
              사용 횟수
            </Typography>
            <Typography variant="body2" sx={{ color: isDarkMode ? '#fff' : '#000', fontWeight: 500, fontSize: '0.75rem' }}>
              {affiliateMaxUses !== undefined && affiliateMaxUses !== null
                ? `${affiliateCurrentUses ?? 0} / ${affiliateMaxUses}`
                : '-'
              }
            </Typography>
          </Box>
        </Box>
      </CardContent>

      {/* Card Actions */}
      <CardActions sx={{ pt: 0, px: 2, pb: 2, gap: 1 }}>
        {/* Wishlist Button */}
        <Button
          size="small"
          variant="outlined"
          onClick={handleWishlistClick}
          disabled={isOwnProduct}
          sx={{
            minWidth: 40,
            px: 1,
            borderColor: isDarkMode ? '#fff' : '#000',
            color: isDarkMode ? '#fff' : '#000',
            opacity: isOwnProduct ? 0.5 : 1,
          }}
        >
          {isInWishlist ? <FavoriteIcon /> : <FavoriteBorderIcon />}
        </Button>

        {/* Request Access Button */}
        <Button
          size="small"
          variant="contained"
          fullWidth
          onClick={handleRequestClick}
          disabled={hasRequested || isOwnProduct}
          startIcon={<ShoppingCartIcon />}
          sx={{
            bgcolor: (hasRequested || isOwnProduct) ? (isDarkMode ? '#333' : '#e0e0e0') : undefined,
          }}
        >
          {isOwnProduct ? '내 상품' : hasRequested ? '요청됨' : '접근 요청'}
        </Button>
      </CardActions>
    </Card>
  );
}
